// Add a dated FX rate, or correct one.
//
// `effective_date` is immutable (D6 / `inputs.ts`): it IS the identity of the
// rate, and moving it would restate every historical report that pinned to it —
// the exact failure the dated table exists to prevent. A wrong date means
// deleting the row and adding the right one, which at least shows up in the
// audit log. So the date is offered on create and shown, disabled, on an edit.

import type { ReactNode } from "react";
import { FxRateCreate, FxRateUpdate } from "@crm/shared";
import type { FxRate } from "@crm/shared";
import { useFxRateMutations } from "../../api/mutations.js";
import { FormDialog } from "../../forms/FormDialog.js";
import { DateField, NumberField, ReadOnlyField } from "../../forms/fields.js";
import { parseDecimal } from "../../forms/parseInput.js";
import { useEditor, type Wire } from "../../forms/useEditor.js";
import { formatCalendarDate } from "../../format/calendarDate.js";
import { formatRate } from "../../format/money.js";

interface FxDraft {
  effective_date: string;
  usd_to_cad: string;
}

const LABELS = { effective_date: "Effective date", usd_to_cad: "USD → CAD" } as const;

function draftFromRecord(rate: FxRate): FxDraft {
  return { effective_date: rate.effective_date, usd_to_cad: String(rate.usd_to_cad) };
}

function toWire(draft: FxDraft): Wire {
  return {
    effective_date: draft.effective_date,
    usd_to_cad: parseDecimal(draft.usd_to_cad),
  };
}

function formatValue(field: string, value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (field === "usd_to_cad" && typeof value === "number") return formatRate(value);
  return String(value);
}

export interface FxRateFormDialogProps {
  /** Absent when adding a rate. */
  rate?: FxRate;
  onClose: () => void;
}

export function FxRateFormDialog({ rate, onClose }: FxRateFormDialogProps): ReactNode {
  const mutations = useFxRateMutations();
  const isNew = rate === undefined;

  const editor = useEditor<FxDraft, FxRateCreate, FxRateUpdate, FxRate>({
    mode: rate === undefined ? { kind: "create" } : { kind: "update", record: rate },
    blankDraft: { effective_date: "", usd_to_cad: "" },
    draftFromRecord,
    toWire,
    createSchema: FxRateCreate,
    updateSchema: FxRateUpdate,
    create: (body, idempotencyKey) => mutations.create.mutateAsync({ body, idempotencyKey }),
    update: (id, body, version) => mutations.update.mutateAsync({ id, body, version }),
    fetchCurrent: mutations.fetchCurrent,
    onSaved: onClose,
  });

  return (
    <FormDialog
      open
      onClose={onClose}
      title={isNew ? "Add an FX rate" : "Edit FX rate"}
      description="Reports convert at the rate in force for the period they cover, not at today's — which is why the table is dated rather than a single setting."
      submitLabel={isNew ? "Add FX rate" : "Save changes"}
      editor={editor}
      labels={LABELS}
      formatValue={formatValue}
    >
      {isNew ? (
        <DateField
          label={LABELS.effective_date}
          required
          hint="From this day forward, reports covering these dates use this rate."
          value={editor.draft.effective_date}
          error={editor.errors["effective_date"]}
          onChange={(value) => editor.setField("effective_date", value)}
        />
      ) : (
        <ReadOnlyField
          label={LABELS.effective_date}
          value={formatCalendarDate(editor.draft.effective_date)}
          reason="The date is this rate's identity. Moving it would restate every report that already pinned to it — delete this row and add the right one instead, which is visible in the audit log."
        />
      )}

      <NumberField
        label={LABELS.usd_to_cad}
        required
        hint="Multiply a USD amount by this to get CAD."
        value={editor.draft.usd_to_cad}
        error={editor.errors["usd_to_cad"]}
        onChange={(value) => editor.setField("usd_to_cad", value)}
      />
    </FormDialog>
  );
}
